/**
 * Error handling utilities for the chat system
 */

export interface CategorizedError {
  title: string;
  description: string;
  action?: 'Retry' | 'Refresh' | 'Wait';
  canRetry: boolean;
}

/**
 * Categorizes errors into user-friendly messages with appropriate actions
 */
export function categorizeError(error: any): CategorizedError {
  const message = error?.message || String(error);
  const errorLower = message.toLowerCase();

  // Timeout errors
  if (errorLower.includes('timeout') || errorLower.includes('etimedout')) {
    return {
      title: 'Request timeout',
      description: 'The AI is taking too long to respond. Try a simpler question or check your connection.',
      action: 'Retry',
      canRetry: true,
    };
  }

  // Network errors
  if (
    errorLower.includes('network') ||
    errorLower.includes('fetch') ||
    errorLower.includes('failed to fetch') ||
    errorLower.includes('networkerror')
  ) {
    return {
      title: 'Network error',
      description: 'Could not connect to the server. Check your internet connection.',
      action: 'Retry',
      canRetry: true,
    };
  }

  // Rate limit errors
  if (errorLower.includes('rate limit') || errorLower.includes('429') || errorLower.includes('too many requests')) {
    return {
      title: 'Too many requests',
      description: "You're sending messages too quickly. Please wait a moment.",
      action: 'Wait',
      canRetry: false,
    };
  }

  // Authentication errors
  if (
    errorLower.includes('401') ||
    errorLower.includes('unauthorized') ||
    errorLower.includes('authentication')
  ) {
    return {
      title: 'Authentication error',
      description: 'Your session may have expired. Please refresh the page.',
      action: 'Refresh',
      canRetry: false,
    };
  }

  // Server errors
  if (
    errorLower.includes('500') ||
    errorLower.includes('502') ||
    errorLower.includes('503') ||
    errorLower.includes('internal server') ||
    errorLower.includes('service unavailable')
  ) {
    return {
      title: 'Server error',
      description: 'The server encountered an error. Please try again in a moment.',
      action: 'Retry',
      canRetry: true,
    };
  }

  // Permission errors
  if (errorLower.includes('403') || errorLower.includes('forbidden') || errorLower.includes('permission')) {
    return {
      title: 'Permission denied',
      description: 'You do not have permission to perform this action.',
      action: undefined,
      canRetry: false,
    };
  }

  // Not found errors
  if (errorLower.includes('404') || errorLower.includes('not found')) {
    return {
      title: 'Resource not found',
      description: 'The requested resource could not be found.',
      action: undefined,
      canRetry: false,
    };
  }

  // Generic error
  return {
    title: 'Something went wrong',
    description: 'An unexpected error occurred. Please try again.',
    action: 'Retry',
    canRetry: true,
  };
}

/**
 * Determines if an error is retryable
 */
export function isRetryableError(error: any): boolean {
  return categorizeError(error).canRetry;
}

/**
 * Gets a user-friendly error message
 */
export function getErrorMessage(error: any): string {
  const categorized = categorizeError(error);
  return `${categorized.title}: ${categorized.description}`;
}
